import { IntlMessageFormat } from "intl-messageformat";
import de from "./locales/de.json" with { type: "json" };
import en from "./locales/en.json" with { type: "json" };

export const LOCALES = ["de", "en"] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = "de";

export const resources: Record<Locale, Record<string, unknown>> = { de, en };

export type Messages = typeof en;

export function isLocale(x: unknown): x is Locale {
  return typeof x === "string" && (LOCALES as readonly string[]).includes(x);
}

/** Pick a locale from an Accept-Language header or device locale string. */
export function pickLocale(input: string | null | undefined): Locale {
  if (!input) return DEFAULT_LOCALE;
  const first = input.split(",")[0]?.trim().toLowerCase() ?? "";
  if (first.startsWith("en")) return "en";
  if (first.startsWith("de")) return "de";
  return DEFAULT_LOCALE;
}

function lookup(obj: Record<string, unknown>, key: string): string | undefined {
  let cur: unknown = obj;
  for (const part of key.split(".")) {
    if (typeof cur !== "object" || cur === null) return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return typeof cur === "string" ? cur : undefined;
}

export type TFunction = (key: string, values?: Record<string, unknown>) => string;

export interface Translator {
  locale: Locale;
  t: TFunction;
}

const cache = new Map<string, IntlMessageFormat>();

/**
 * Create a translator for one locale. ICU MessageFormat (plurals, selects) via intl-messageformat.
 * Missing keys fall back to `de`, then to the key itself (never throws in production paths).
 */
export function createTranslator(locale: Locale = DEFAULT_LOCALE): Translator {
  const t: TFunction = (key, values) => {
    const msg = lookup(resources[locale], key) ?? lookup(resources.de, key);
    if (msg === undefined) return key;
    if (!values) return msg;
    const cacheKey = `${locale}:${key}`;
    let fmt = cache.get(cacheKey);
    if (!fmt || fmt.resolvedOptions().locale !== locale) {
      fmt = new IntlMessageFormat(msg, locale);
      cache.set(cacheKey, fmt);
    }
    try {
      return String(fmt.format(values as Record<string, string | number>));
    } catch {
      return msg;
    }
  };
  return { locale, t };
}

/** Resolve a localized jsonb column ({de, en}) with fallback. */
export function pickText(
  value: Record<string, string> | null | undefined,
  locale: Locale,
): string {
  if (!value) return "";
  return value[locale] ?? value.de ?? value.en ?? Object.values(value)[0] ?? "";
}

export {
  decodeFeedPostBody,
  encodeFeedPostBody,
  encodeStoredLocalized,
  parseStoredLocalized,
  FEED_LOCALIZED_PREFIX,
  type LocalizedPlain,
} from "./feedPostText.js";
